import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { TextField } from "src/shared/ui/TextField";
import { FriendItem } from "./FriendItem";
import useStores from "../../../../hooks/useStores";
import { FriendTypeEnum, IFriend, ISearchedUser } from "../../../../store/models";
import { debounce } from "lodash";
import { useStyles } from "./styles";
import { useLocale } from "../../../../providers/LocaleProvider";
import friendsIcon from "src/assets/icons/UsersThree.svg";

interface MobileFriendsProps {
    onClose: () => void;
}

const MobileFriends: React.FC<MobileFriendsProps> = observer(({ onClose }) => {
    const classes = useStyles();
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<ISearchedUser[]>([]);
    const { friendsStore, authStore } = useStores();
    const [isSearching, setIsSearching] = useState(false);
    const [sentRequests, setSentRequests] = useState<string[]>([]);

    const {
        lobby: {
            tabHeader: { rank, frenemies, soon, search, textArea, noUser, searchArea },
        },
    } = useLocale();

    useEffect(() => {
        updateFriendsList();
        const intervalId = setInterval(updateFriendsList, 10000);
        return () => clearInterval(intervalId);
    }, [friendsStore]);


    const updateFriendsList = async () => {
        try {
            await friendsStore.getFriends();
        } catch (error) {
            console.error('Error updating friends list:', error);
        }
    };

    const handleClose = () => {
        onClose();
    };

    const handleSearch = debounce(async (query: string) => {
        if (query.length >= 1) {
            try {
                setIsSearching(true);
                const results = await friendsStore.searchUsers(query);
                const filteredResults = results.filter(user =>
                    user.id !== authStore.currentUser?._id
                );
                setSearchResults(filteredResults);
            } catch (error) {
                console.error('Search error:', error);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        } else {
            setSearchResults([]);
        }
    }, 300);

    const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setSearchQuery(query);
        handleSearch(query);
    };

    const handleAddFriend = async (friendId: string) => {
        try {
            setSearchQuery("");
            setSearchResults([]);
            const response = await friendsStore.addFriend({ playerTo: friendId });
            if (response?.success) {
                await friendsStore.getFriends();
            }
        } catch (error) {
            console.error('Error while sending friend request:', error);
        }
    };

    const handleDeleteFriend = async (friendId: string) => {
        try {
            const response = await friendsStore.deleteFriend({ playerTo: friendId });
            if (response?.success) {
                await friendsStore.getFriends();
            }
        } catch (error) {
            console.error('Error while deleting friend:', error);
        }
    };

    const handleAcceptFriend = async (friendId: string) => {
        try {
            const response = await friendsStore.approveFriendRequest({ id: friendId });
            if (response) {
                await updateFriendsList();
            }
        } catch (error) {
            console.error('Error while accepting friend request:', error);
        }
    };

    const handleRejectFriend = async (friendId: string) => {
        try {
            const response = await friendsStore.rejectFriendRequest({ playerTo: friendId });
            if (response?.success) {
                setSentRequests(prev => prev.filter(id => id !== friendId));
                await updateFriendsList();
            }
        } catch (error) {
            console.error('Error while rejecting friend request:', error);
        }
    };

    const renderContent = () => {
        if (searchQuery.length >= 1) {
            if (isSearching) {
                return <div className={classes.searchMessage}>{searchArea('Searching...')}</div>;
            }
            if (searchResults.length > 0) {
                return (
                    <div className={classes.listWrapper}>
                        {searchResults.map((user: ISearchedUser) => {
                            const existingFriend = friendsStore.friends.find(
                                friend => friend.user._id === user.id &&
                                    (friend.type === FriendTypeEnum.friend || friend.type === FriendTypeEnum.outgoing)
                            );
                            if (existingFriend) {
                                return (
                                    <FriendItem
                                        key={existingFriend.user._id}
                                        item={existingFriend}
                                        sentRequests={friendsStore.sentRequests}
                                        onDelete={() => handleDeleteFriend(existingFriend.user._id)}
                                    />
                                );
                            }
                            return (
                                <FriendItem
                                    key={user.id}
                                    item={user}
                                    onAdd={() => handleAddFriend(user.id)}
                                    sentRequests={friendsStore.sentRequests}
                                />
                            );
                        })}
                    </div>
                );
            }
            return <div className={classes.searchMessage}>{noUser('No users found')}</div>;
        }

        const sortByName = (a: IFriend, b: IFriend) => {
            const nameA = a.user.nickname.toLowerCase();
            const nameB = b.user.nickname.toLowerCase();
            return nameA.localeCompare(nameB);
        };

        const incomingRequests = friendsStore.friends
            .filter(friend => friend.type === FriendTypeEnum.incoming)
            .sort(sortByName);

        const outgoingRequests = friendsStore.friends
            .filter(friend => friend.type === FriendTypeEnum.outgoing)
            .sort(sortByName);

        const friends = friendsStore.friends.filter(
            friend => friend.type === FriendTypeEnum.friend
        );

        const onlineFriends = friends
            .filter(friend => friend.online)
            .sort(sortByName);

        const offlineFriends = friends
            .filter(friend => !friend.online)
            .sort(sortByName);

        const sortedList = [
            ...incomingRequests,
            ...outgoingRequests,
            ...onlineFriends,
            ...offlineFriends
        ];

        if (sortedList.length === 0) {
            return (
                <div className={classes.noFriends}>
                    {textArea('No friends yet.Use the search to find and add friends!')}
                </div>
            );
        }

        return (
            <>
                <div className={classes.listWrapper}>
                    {sortedList.map((friend: IFriend) => {
                        if (friend.type === FriendTypeEnum.incoming) {
                            return (
                                <FriendItem
                                    key={friend.user._id}
                                    item={friend}
                                    onAccept={() => handleAcceptFriend(friend.user._id)}
                                    onRemove={() => handleRejectFriend(friend.user._id)}
                                    sentRequests={sentRequests}
                                />
                            );
                        }
                        if (friend.type === FriendTypeEnum.outgoing) {
                            return (
                                <FriendItem
                                    key={friend.user._id}
                                    item={friend}
                                    sentRequests={friendsStore.sentRequests}
                                    onDelete={() => handleDeleteFriend(friend.user._id)}
                                />
                            );
                        }
                        return (
                            <FriendItem
                                key={friend.user._id}
                                item={friend}
                                sentRequests={sentRequests}
                                onDelete={() => handleDeleteFriend(friend.user._id)}
                            />
                        );
                    })}
                </div>
            </>
        );
    };

    return (
        <div className={classes.mobileOverlay}>
            <div className={classes.mobileContainer}>
                <div className={classes.header}>
                    <img src={friendsIcon}></img>
                    <div className={classes.title}>{frenemies("Frienemies")}</div>
                    <button className={classes.closeButton} onClick={handleClose}>
                        ✕
                    </button>
                </div>

                <div className={classes.searchContainer}>
                    <TextField
                        value={searchQuery}
                        onChange={handleSearchInputChange}
                        type="search"
                        placeholder={search("Search Frenemies")}
                    />
                </div>

                <div className={classes.content}>
                    {renderContent()}
                </div>
            </div>
        </div>
    );
});

export default MobileFriends;